import { createRouter, createWebHistory } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore } from '@/stores/permissions'

const routes = [
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/public/HomeView.vue')
      },
      {
        path: 'rooms',
        name: 'rooms',
        component: () => import('@/views/public/RoomsView.vue')
      },
      {
        path: 'rooms/:id',
        name: 'room-detail',
        component: () => import('@/views/public/RoomDetailView.vue')
      },
      {
        path: 'book/callback',
        name: 'booking-callback',
        component: () => import('@/views/public/BookingCallbackView.vue')
      },
      {
        path: 'book/:id',
        name: 'booking',
        component: () => import('@/views/public/BookingView.vue')
      },
      {
        path: 'booking-confirmed',
        name: 'booking-confirmed',
        component: () => import('@/views/public/BookingConfirmationView.vue')
      },
      {
        path: 'gallery',
        name: 'gallery',
        component: () => import('@/views/public/GalleryView.vue')
      },
      {
        path: 'tour',
        name: 'tour',
        component: () => import('@/views/public/HotelTourView.vue')
      },
      {
        path: 'about',
        name: 'about',
        component: () => import('@/views/public/SitePageView.vue'),
        props: { page: 'about' }
      },
      {
        path: 'contact',
        name: 'contact',
        component: () => import('@/views/public/ContactView.vue')
      },
      {
        path: 'blog',
        name: 'blog',
        component: () => import('@/views/public/BlogView.vue')
      },
      {
        path: 'blog/category/:slug',
        name: 'blog-category',
        component: () => import('@/views/public/BlogView.vue')
      },
      {
        path: 'blog/:slug',
        name: 'blog-post',
        component: () => import('@/views/public/BlogPostView.vue')
      },
      {
        path: 'privacy',
        name: 'privacy',
        component: () => import('@/views/public/SitePageView.vue'),
        props: { page: 'privacy' }
      },
      {
        path: 'terms',
        name: 'terms',
        component: () => import('@/views/public/SitePageView.vue'),
        props: { page: 'terms' }
      }
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { guestOnly: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue')
  },
  {
    path: '/app',
    component: () => import('@/layouts/PortalLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue')
      },
      {
        path: 'guests',
        name: 'guests',
        component: () => import('@/views/staff/guests/GuestListView.vue'),
        meta: { permission: 'guests.view' }
      },
      {
        path: 'guests/:id',
        name: 'guest-detail',
        component: () => import('@/views/staff/guests/GuestDetailView.vue'),
        meta: { permission: 'guests.view' }
      },
      {
        path: 'room-types',
        name: 'room-types',
        component: () => import('@/views/staff/rooms/RoomTypeListView.vue'),
        meta: { permission: 'rooms.view' }
      },
      {
        path: 'rooms',
        name: 'manage-rooms',
        component: () => import('@/views/staff/rooms/RoomListView.vue'),
        meta: { permission: 'rooms.view' }
      },
      {
        path: 'reservations',
        name: 'reservations',
        component: () =>
          import('@/views/staff/reservations/ReservationListView.vue'),
        meta: { permission: 'reservations.view' }
      },
      {
        path: 'reservations/:id',
        name: 'reservation-detail',
        component: () =>
          import('@/views/staff/reservations/ReservationDetailView.vue'),
        meta: { permission: 'reservations.view' }
      },
      {
        path: 'reservations/:id/folio',
        name: 'folio',
        component: () => import('@/views/staff/folio/FolioView.vue'),
        meta: { permission: 'folio.view' }
      },
      {
        path: 'reservations/:id/checkout',
        name: 'checkout',
        component: () => import('@/views/staff/checkout/CheckoutView.vue'),
        meta: { permission: 'checkout.view' }
      },
      {
        path: 'restaurant/menu',
        name: 'menu',
        component: () => import('@/views/staff/restaurant/MenuView.vue'),
        meta: { permission: 'restaurant.view' }
      },
      {
        path: 'restaurant/orders',
        name: 'restaurant-orders',
        component: () =>
          import('@/views/staff/restaurant/RestaurantOrdersView.vue'),
        meta: { permission: 'restaurant.view' }
      },
      {
        path: 'bar',
        name: 'bar',
        component: () => import('@/views/staff/bar/BarView.vue'),
        meta: { permission: 'bar.view' }
      },
      {
        path: 'room-service',
        name: 'room-service',
        component: () => import('@/views/staff/roomservice/RoomServiceView.vue'),
        meta: { permission: 'room_service.view' }
      },
      {
        path: 'laundry',
        name: 'laundry',
        component: () => import('@/views/staff/laundry/LaundryView.vue'),
        meta: { permission: 'laundry.view' }
      },
      {
        path: 'housekeeping',
        name: 'housekeeping',
        component: () =>
          import('@/views/staff/housekeeping/HousekeepingView.vue'),
        meta: { permission: 'housekeeping.view' }
      },
      {
        path: 'inventory',
        name: 'inventory',
        component: () => import('@/views/staff/inventory/InventoryView.vue'),
        meta: { permission: 'inventory.view' }
      },
      {
        path: 'inventory/:id/stock',
        name: 'inventory-stock',
        component: () => import('@/views/staff/inventory/StockView.vue'),
        meta: { permission: 'inventory.view' }
      },
      {
        path: 'suppliers',
        name: 'suppliers',
        component: () => import('@/views/staff/suppliers/SupplierView.vue'),
        meta: { permission: 'suppliers.view' }
      },
      {
        path: 'purchase-orders',
        name: 'purchase-orders',
        component: () =>
          import('@/views/staff/suppliers/PurchaseOrdersView.vue'),
        meta: { permission: 'purchase_orders.view' }
      },
      {
        path: 'payments',
        name: 'payments',
        component: () => import('@/views/staff/payments/PaymentsView.vue'),
        meta: { permission: 'payments.view' }
      },
      {
        path: 'accounting',
        name: 'accounting',
        component: () => import('@/views/staff/accounting/AccountingView.vue'),
        meta: { permission: 'accounting.view' }
      },
      {
        path: 'financial-analytics',
        name: 'financial-analytics',
        component: () =>
          import('@/views/staff/accounting/FinancialAnalyticsView.vue'),
        meta: { permission: 'accounting.view' }
      },
      {
        path: 'expenses',
        name: 'expenses',
        component: () => import('@/views/staff/accounting/ExpensesView.vue'),
        meta: { permission: 'expenses.view' }
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/staff/reports/ReportsView.vue'),
        meta: { permission: 'reports.view' }
      },
      {
        path: 'audit-log',
        name: 'audit-log',
        component: () => import('@/views/staff/audit/AuditLogView.vue'),
        meta: { permission: 'audit.view' }
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () =>
          import('@/views/staff/notifications/NotificationsView.vue'),
        meta: { permission: 'notifications.view' }
      },
      {
        path: 'roles',
        name: 'roles',
        component: () => import('@/views/staff/admin/RolesView.vue'),
        meta: { permission: 'roles.view' }
      },
      {
        path: 'roles/:id/permissions',
        name: 'role-permissions',
        component: () => import('@/views/staff/admin/RolePermissionsView.vue'),
        meta: { permission: 'roles.update' }
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/staff/admin/UsersView.vue'),
        meta: { permission: 'users.view' }
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/staff/admin/SettingsView.vue'),
        meta: { permission: 'settings.view' }
      },
      {
        path: 'website-cms',
        name: 'website-cms',
        component: () => import('@/views/staff/admin/WebsiteCmsView.vue'),
        meta: { permission: 'settings.view' }
      },
      {
        path: 'gallery',
        name: 'gallery-manager',
        component: () => import('@/views/staff/admin/GalleryManagerView.vue'),
        meta: { permission: 'settings.view' }
      },
      {
        path: 'blog',
        name: 'blog-admin',
        component: () => import('@/views/staff/admin/BlogAdminView.vue'),
        meta: { permission: 'settings.view' }
      },
      {
        path: 'tour-videos',
        name: 'tour-admin',
        component: () => import('@/views/staff/admin/HotelTourView.vue'),
        meta: { permission: 'settings.view' }
      },
      {
        path: 'contact-messages',
        name: 'contact-messages',
        component: () => import('@/views/staff/admin/ContactMessagesView.vue'),
        meta: { permission: 'settings.view' }
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/staff/ProfileView.vue')
      },
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/ForbiddenView.vue')
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// Auth + dynamic RBAC guard. Routes declare meta.requiresAuth and, optionally,
// meta.permission (a code or array of codes — any match grants access).
router.beforeEach((to) => {
  const auth = useAuthStore()

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.permission) {
    const permissions = usePermissionsStore()
    const required = Array.isArray(to.meta.permission)
      ? to.meta.permission
      : [to.meta.permission]
    if (!required.some((code) => permissions.can(code))) {
      return { name: 'forbidden' }
    }
  }

  return true
})

export default router
